import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import TextInput from "../../components/TextInput/TextInput";
import Collapse from "@mui/material/Collapse";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PropTypes from "prop-types";
import MemberUserController from "./Controller/member_user_controller.ts";
import { notification } from "antd";
import { useSearchParams } from "react-router-dom";
import SelectInput from "../../components/TextInput/SelectInput";

// import InfinityEncryptor from "../../components/EncryptDecrypt/InfinityEncryptor.js";

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const ExpandMore = styled((props) => {
  const { expand, ...other } = props;
  return <div {...other} />;
})(({ theme }) => ({
  marginLeft: "auto",
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shortest,
  }),
  variants: [
    {
      props: ({ expand }) => !expand,
      style: {
        transform: "rotate(0deg)",
      },
    },
    {
      props: ({ expand }) => !!expand,
      style: {
        transform: "rotate(180deg)",
      },
    },
  ],
}));

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

const Dashboard = () => {
  // expand button
  const [expanded, setExpanded] = React.useState(false);
  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  // notification
  const [api, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = (type, desc, msg) => {
    api[type]({
      message: msg,
      description: desc,
    });
  };
  // encryptor
  // const key = "12345";
  // const mode = "YMDHI";
  // const Encryptor = new InfinityEncryptor(key, mode);

  const [shouldRefetchData, setShouldRefetchData] = useState(false);
  const [data, setData] = useState([]); //payment is default during registration eg: registration fee & first year annual subscription fee

  const [searchParams] = useSearchParams();
  const userID = searchParams.get("userID");
  useEffect(() => {
    if (userID) {
      // const payload = {
      //   userID: userID,
      // };
      const payload = new FormData();
      payload.append("userID", userID);
      MemberUserController.getUserInfo(payload)
        .then((response) => {
          console.log(response);
          setData(response);
        })
        .catch((error) => {
          console.error("Error fetching data:", error);
        });
    }
  }, [userID, shouldRefetchData]);

  const {
    handleSubmit,
    register,
    formState: { errors },
    control,
    reset,
  } = useForm({
    defaultValues: {
      name: "",
      nric: "",
      nationality: "",
      date_of_birth: "",
      place_of_birth: "",
      race: "",
      gender: "",
      marital_status: "",
      office_tel_no: "",
      fax: "",
      handphone_no: "",
      email: "",
      designation: "",
      nature_of_work: "",
      years_with_company: "",
      academic_qualification: "",
      committee_typeID: "",
      type: "", // account type
    },
  });

  useEffect(() => {
    if (data) {
      reset({
        name: data.name || "",
        nric: data.nric || "",
        nationality: data.nationality || "",
        date_of_birth: data.date_of_birth || "",
        place_of_birth: data.place_of_birth || "",
        race: data.race || "",
        gender: data.gender || "",
        marital_status: data.marital_status || "",
        office_tel_no: data.office_tel_no || "",
        fax: data.fax || "",
        handphone_no: data.handphone_no || "",
        email: data.email || "",
        designation: data.designation || "",
        nature_of_work: data.nature_of_work || "",
        years_with_company: data.years_with_company || "",
        academic_qualification: data.academic_qualification || "",
        committee_typeID: data.committee_typeID || "",
        type: data.type || "", // account type
      });
    }
  }, [data, reset]);

  const onSubmit = async (data) => {
    console.log(data);
    const payload = {
      ID: userID,
      data: data,
    };
    if (Object.keys(errors).length > 0) {
      console.log("Form has errors:", errors);
      return; // Prevent submission if there are errors
    }

    try {
      const response = await MemberUserController.updateMemberUser(payload);
      console.log(response);
      if (response?.status === "success") {
        openNotificationWithIcon(
          "success",
          "Your data has been saved successfully!",
          "Success"
        );
        setShouldRefetchData((prev) => !prev); // Toggle to re-trigger useEffect
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const onInvalid = (errors) => {
    console.log("Form has errors:", errors);
  };

  return (
    <Box
      sx={{
        display: "flex",
        backgroundColor: "#e8e8e8",
        height: expanded ? "300vh" : "100vh",
        width: "100vw",
      }}
    >
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
        }}
      >
        <DrawerHeader />
        {contextHolder}

        <Card variant="outlined">
          <Grid container spacing={2} sx={{ p: 1 }}>
            <Grid size={6}>
              <Typography variant="h7">Name: {data.name}</Typography>
            </Grid>
            <Grid size={6}>
              <Typography variant="h7" component="div">
                NRIC: {data.nric}
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ p: 1 }}>
            <Grid size={6}>
              <Typography variant="h7">
                Nationality: {data.nationality}
              </Typography>
            </Grid>
            <Grid size={6}>
              <Typography variant="h7" component="div">
                Date of Birth: {data.date_of_birth}
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ p: 1 }}>
            <Grid size={6}>
              <Typography variant="h7">
                Place Of Birth: {data.place_of_birth}
              </Typography>
            </Grid>
            <Grid size={6}>
              <Typography variant="h7" component="div">
                Race:{" "}
                {data.race === "0"
                  ? "Malay"
                  : data.gender === "1"
                  ? "Chinese"
                  : data.gender === "2"
                  ? "Indian"
                  : data.gender === "3"
                  ? "Indigenous Peoples"
                  : data.gender === "4"
                  ? "Others"
                  : null}
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ p: 1 }}>
            <Grid size={6}>
              <Typography variant="h7">
                Gender:{" "}
                {data.gender === "0"
                  ? "Female"
                  : data.gender === "1"
                  ? "Male"
                  : null}
              </Typography>
            </Grid>
            <Grid size={6}>
              <Typography variant="h7" component="div">
                Marital Status:{" "}
                {data.marital_status === "0"
                  ? "Single"
                  : data.marital_status === "1"
                  ? "Married"
                  : data.marital_status === "2"
                  ? "Divorced"
                  : data.marital_status === "3"
                  ? "Widowed"
                  : data.marital_status === "4"
                  ? "Separated"
                  : null}
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ p: 1 }}>
            <Grid size={6}>
              <Typography variant="h7">
                Office Tel No: {data.office_tel_no}
              </Typography>
            </Grid>
            <Grid size={6}>
              <Typography variant="h7" component="div">
                Fax: {data.fax}
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ p: 1 }}>
            <Grid size={6}>
              <Typography variant="h7">H/P: {data.handphone_no}</Typography>
            </Grid>
            <Grid size={6}>
              <Typography variant="h7" component="div">
                Email: {data.email}
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ p: 1 }}>
            <Grid size={6}>
              <Typography variant="h7">
                Designation: {data.designation}
              </Typography>
            </Grid>
            <Grid size={6}>
              <Typography variant="h7" component="div">
                Nature of Work: {data.nature_of_work}
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ p: 1 }}>
            <Grid size={6}>
              <Typography variant="h7">
                Years with Company: {data.years_with_company}
              </Typography>
            </Grid>
            <Grid size={6}>
              <Typography variant="h7" component="div">
                Academic Qualification:{" "}
                {data.academic_qualification === "0"
                  ? "None"
                  : data.academic_qualification === "1"
                  ? "Certificate"
                  : data.academic_qualification === "2"
                  ? "Diploma"
                  : data.academic_qualification === "3"
                  ? "Advanced Diploma"
                  : data.academic_qualification === "4"
                  ? "Bachelor"
                  : data.academic_qualification === "5"
                  ? "Masters"
                  : data.academic_qualification === "6"
                  ? "Doctoral"
                  : null}
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ p: 1 }}>
            <Grid size={6}>
              <Typography variant="h7">
                Committee Type: {data.committee_type_label}
              </Typography>
            </Grid>
            <Grid size={6}>
              <Typography variant="h7" component="div">
                Account Type:{" "}
                {data.type === 0
                  ? "Main Account"
                  : data.type === 1
                  ? "Secondary Account"
                  : ""}
              </Typography>
            </Grid>
          </Grid>

          <CardActions>
            <Button onClick={handleExpandClick} size="small">
              Edit
              <ExpandMore
                expand={expanded}
                aria-expanded={expanded}
                aria-label="show more"
              >
                <ExpandMoreIcon />
              </ExpandMore>
            </Button>
          </CardActions>
        </Card>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <CardContent>
            <form
              onSubmit={handleSubmit(onSubmit, onInvalid, {
                shouldFocusError: true,
              })}
            >
              <Grid container spacing={2} sx={{ p: 2 }}>
                <Grid size={4}>
                  <Typography variant="h6">Name</Typography>
                </Grid>
                <Grid size={8}>
                  <TextInput
                    type="text"
                    {...register("name", {
                      required: "This field is required",
                    })}
                    onErrorCheck={errors ? "" : ""}
                    control={control}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ p: 2 }}>
                <Grid size={4}>
                  <Typography variant="h6">NRIC</Typography>
                </Grid>
                <Grid size={8}>
                  <TextInput
                    type="text"
                    {...register("nric", {
                      required: "This field is required",
                    })}
                    onErrorCheck={errors ? "" : ""}
                    control={control}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ p: 2 }}>
                <Grid size={4}>
                  <Typography variant="h6">Nationality</Typography>
                </Grid>
                <Grid size={8}>
                  <TextInput
                    type="text"
                    {...register("nationality", {
                      required: "This field is required",
                    })}
                    onErrorCheck={errors ? "" : ""}
                    control={control}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ p: 2 }}>
                <Grid size={4}>
                  <Typography variant="h6">Date of Birth</Typography>
                </Grid>
                <Grid size={8}>
                  <TextInput
                    type="text"
                    {...register("date_of_birth", {
                      required: "This field is required",
                    })}
                    onErrorCheck={errors ? "" : ""}
                    control={control}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ p: 2 }}>
                <Grid size={4}>
                  <Typography variant="h6">Place of Birth</Typography>
                </Grid>
                <Grid size={8}>
                  <TextInput
                    type="text"
                    {...register("place_of_birth", {
                      required: "This field is required",
                    })}
                    onErrorCheck={errors ? "" : ""}
                    control={control}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ p: 2 }}>
                <Grid size={4}>
                  <Typography variant="h6">Race</Typography>
                </Grid>
                <Grid size={8}>
                  <SelectInput
                    type="text"
                    {...register("race", {
                      required: false,
                    })}
                    onErrorCheck={errors.race ? errors.race.message : ""}
                    control={control}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ p: 2 }}>
                <Grid size={4}>
                  <Typography variant="h6">Gender</Typography>
                </Grid>
                <Grid size={8}>
                  <SelectInput
                    type="text"
                    {...register("gender", {
                      required: false,
                    })}
                    onErrorCheck={errors.gender ? errors.gender.message : ""}
                    control={control}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ p: 2 }}>
                <Grid size={4}>
                  <Typography variant="h6">Marital Status</Typography>
                </Grid>
                <Grid size={8}>
                  <SelectInput
                    type="text"
                    {...register("marital_status", {
                      required: false,
                    })}
                    onErrorCheck={
                      errors.marital_status ? errors.marital_status.message : ""
                    }
                    control={control}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ p: 2 }}>
                <Grid size={4}>
                  <Typography variant="h6">Office Tel No</Typography>
                </Grid>
                <Grid size={8}>
                  <TextInput
                    type="text"
                    {...register("office_tel_no", {
                      required: "This field is required",
                    })}
                    onErrorCheck={errors ? errors.office_tel_no : ""}
                    control={control}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ p: 2 }}>
                <Grid size={4}>
                  <Typography variant="h6">Fax</Typography>
                </Grid>
                <Grid size={8}>
                  <TextInput
                    type="text"
                    {...register("fax", {
                      required: "This field is required",
                    })}
                    onErrorCheck={errors ? "" : ""}
                    control={control}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ p: 2 }}>
                <Grid size={4}>
                  <Typography variant="h6">H/P</Typography>
                </Grid>
                <Grid size={8}>
                  <TextInput
                    type="text"
                    {...register("handphone_no", {
                      required: "This field is required",
                    })}
                    onErrorCheck={errors ? "" : ""}
                    control={control}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ p: 2 }}>
                <Grid size={4}>
                  <Typography variant="h6">Email</Typography>
                </Grid>
                <Grid size={8}>
                  <TextInput
                    type="text"
                    {...register("email", {
                      required: "This field is required",
                    })}
                    onErrorCheck={errors ? errors.email : ""}
                    control={control}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ p: 2 }}>
                <Grid size={4}>
                  <Typography variant="h6">Designation</Typography>
                </Grid>
                <Grid size={8}>
                  <TextInput
                    type="text"
                    {...register("designation", {
                      required: "This field is required",
                    })}
                    onErrorCheck={errors ? errors.designation : ""}
                    control={control}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ p: 2 }}>
                <Grid size={4}>
                  <Typography variant="h6">Nature of Work</Typography>
                </Grid>
                <Grid size={8}>
                  <TextInput
                    type="text"
                    {...register("nature_of_work", {
                      required: "This field is required",
                    })}
                    onErrorCheck={errors ? errors.nature_of_work : ""}
                    control={control}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ p: 2 }}>
                <Grid size={4}>
                  <Typography variant="h6">Years with Company</Typography>
                </Grid>
                <Grid size={8}>
                  <TextInput
                    type="text"
                    {...register("years_with_company", {
                      required: "This field is required",
                    })}
                    onErrorCheck={errors ? errors.years_with_company : ""}
                    control={control}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ p: 2 }}>
                <Grid size={4}>
                  <Typography variant="h6">Academic Qualification</Typography>
                </Grid>
                <Grid size={8}>
                  <SelectInput
                    type="text"
                    {...register("academic_qualification", {
                      required: false,
                    })}
                    onErrorCheck={
                      errors.academic_qualification
                        ? errors.academic_qualification.message
                        : ""
                    }
                    control={control}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ p: 2 }}>
                <Grid size={4}>
                  <Typography variant="h6">Committee Type</Typography>
                </Grid>
                <Grid size={8}>
                  <SelectInput
                    type="text"
                    {...register("committee_typeID", {
                      required: "This field is required",
                    })}
                    onErrorCheck={
                      errors.committee_typeID
                        ? errors.committee_typeID.message
                        : ""
                    }
                    control={control}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ p: 2 }}>
                <Grid size={4}>
                  <Typography variant="h6">Account Type</Typography>
                </Grid>
                <Grid size={8}>
                  <SelectInput
                    type="text"
                    {...register("type", {
                      required: "This field is required",
                    })}
                    onErrorCheck={errors.type ? errors.type.message : ""}
                    control={control}
                  />
                </Grid>
              </Grid>

              <Button
                sx={{
                  backgroundColor: "#d33333",
                }}
                variant="contained"
                type="submit"
              >
                <Typography variant="h6">Save</Typography>
              </Button>
            </form>
          </CardContent>
        </Collapse>
      </Box>
    </Box>
  );
};

export default Dashboard;
